/**
 * Eine Unterlage ansehen — der Dialog „Beleg ansehen" aus der Vorlage, für
 * hochgeladene Dokumente.
 *
 * Links das Dokument (PDF im eigenen Betrachter, Bilder direkt, alles
 * andere als Download). Rechts drei Karten, wie im Prototyp: die
 * Dokumentdetails, der Prüfvermerk des Beirats mit den beiden Handlungen
 * (als geprüft markieren, Rückfrage an die Verwaltung) und die
 * Prüfhistorie — wer hat wann was damit gemacht.
 *
 * Der Vermerk wird beim Verlassen des Feldes gespeichert, nicht bei jedem
 * Tastendruck, und erscheint im Protokoll neben der Unterlage.
 */

import { useEffect, useState } from 'react';

import { api, datum } from '../lib/api';
import { markierungLesen, type Dokument, type Kategorie, type Kommentar, type PdfRechteck, type PruefStatus, type Rolle } from '../lib/typen';
import { AssistentGespraech, AssistentSymbol, type Nachricht } from './Assistent';
import { Haken } from './Haken';
import { PdfAnsicht } from './PdfAnsicht';

export interface DokumentansichtProps {
  pruefungId: string;
  dokument: Dokument;
  kategorie: Kategorie | undefined;
  rolle: Rolle;
  abgeschlossen: boolean;
  /** Rückfragen und Antworten zu dieser Unterlage, für die Historie. */
  kommentare?: Kommentar[];
  onStatus: (dokumentId: string, status: PruefStatus) => void;
  onVermerk?: (dokumentId: string, vermerk: string) => Promise<void> | void;
  onRueckfrage: (dokumentId: string, markierung?: PdfRechteck) => void;
  onSchliessen: () => void;
  /** Eine markierte Stelle, die beim Öffnen gezeigt wird (aus einer Rückfrage). */
  stelle?: PdfRechteck | null;
  /** Ob der Prüfassistent eingerichtet ist (null: noch unbekannt). */
  assistent?: boolean | null;
  /** Blättern in der Liste, aus der der Dialog geöffnet wurde. */
  onWeiter?: () => void;
  onZurueck?: () => void;
}

export function Dokumentansicht({
  pruefungId,
  dokument,
  kategorie,
  rolle,
  abgeschlossen,
  kommentare = [],
  onStatus,
  onVermerk,
  onRueckfrage,
  onSchliessen,
  onWeiter,
  onZurueck,
  assistent = null,
  stelle = null,
}: DokumentansichtProps) {
  const [hervorhebung, setHervorhebung] = useState<PdfRechteck | null>(stelle);
  useEffect(() => {
    setHervorhebung(stelle);
  }, [stelle, dokument.id]);
  /** Rückfragen zu dieser Unterlage, die eine markierte Stelle haben. */
  const markierteStellen = kommentare
    .filter((k) => k.dokument_id === dokument.id && !k.eltern_id)
    .map((k) => ({ k, m: markierungLesen(k) }))
    .filter((x): x is { k: Kommentar; m: PdfRechteck } => x.m !== null);
  const [gespraech, setGespraech] = useState<Nachricht[]>([]);
  const [frageOffen, setFrageOffen] = useState(false);
  const [drehung, setDrehung] = useState<0 | 90 | 180 | 270>(0);
  /** Halbe Breite vom rechten Rand (Standard) oder Vollbild — der Browser merkt sich die Wahl. */
  const [voll, setVoll] = useState<boolean>(() => {
    try {
      return localStorage.getItem('bp_dok_voll') === '1';
    } catch {
      return false;
    }
  });
  const [vermerk, setVermerk] = useState(dokument.vermerk ?? '');
  const [gespeichert, setGespeichert] = useState<'nein' | 'laeuft' | 'ja'>('nein');
  const url = api.dokumentUrl(pruefungId, dokument.id);
  const istPdf = dokument.typ === 'application/pdf';
  const istBild = dokument.typ.startsWith('image/');
  const istBeirat = rolle === 'beirat';
  const darfVermerken = istBeirat && !abgeschlossen && Boolean(onVermerk);

  useEffect(() => {
    setVermerk(dokument.vermerk ?? '');
    setGespeichert('nein');
    setDrehung(0);
  }, [dokument.id, dokument.vermerk]);

  // Ein anderes Dokument, ein neues Gespräch.
  useEffect(() => {
    setGespraech([]);
  }, [dokument.id]);

  useEffect(() => {
    function taste(e: KeyboardEvent) {
      if (e.key === 'Escape') onSchliessen();
    }
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [onSchliessen]);

  function vollSetzen(wert: boolean) {
    setVoll(wert);
    try {
      localStorage.setItem('bp_dok_voll', wert ? '1' : '0');
    } catch {
      // Nur eine Bequemlichkeit.
    }
  }

  async function vermerkSpeichern() {
    if (!onVermerk || vermerk === (dokument.vermerk ?? '')) return;
    setGespeichert('laeuft');
    try {
      await onVermerk(dokument.id, vermerk);
      setGespeichert('ja');
    } catch {
      setGespeichert('nein');
    }
  }

  const historie: { wann: string; was: string }[] = [
    { wann: dokument.hochgeladenAm, was: `Bereitgestellt durch die Verwaltung${dokument.hochgeladenVon ? ` (${dokument.hochgeladenVon})` : ''}` },
    ...kommentare
      .filter((k) => k.dokument_id === dokument.id)
      .map((k) => ({
        wann: k.erstellt_am,
        was: k.eltern_id ? `Antwort von ${k.autor_name}` : `Rückfrage von ${k.autor_name}${k.status === 'beantwortet' ? ' — beantwortet' : k.status === 'erledigt' ? ' — erledigt' : ''}`,
      })),
    ...(dokument.pruefstatus === 'geprueft' && dokument.pruefstatusAm
      ? [{ wann: dokument.pruefstatusAm, was: `Als geprüft markiert${dokument.pruefstatusVon ? ` von ${dokument.pruefstatusVon}` : ''}` }]
      : []),
  ].sort((a, b) => a.wann.localeCompare(b.wann));

  return (
    <div className={`dok-lade${voll ? ' voll' : ''}`} role="dialog" aria-label={dokument.dateiname}>
      <div className="dok-dialog">
        <div className="dok-kopf">
          <div className="dok-titel-zeile">
            <h3 className="dok-titel" title={dokument.dateiname}>
              {kategorie?.bezeichnung ?? 'Unterlage'}
            </h3>
            <span className={`chip${dokument.pruefstatus === 'geprueft' ? ' ok' : dokument.pruefstatus === 'rueckfrage' ? ' warn' : ''}`}>
              {dokument.pruefstatus === 'geprueft' ? 'Geprüft' : dokument.pruefstatus === 'rueckfrage' ? 'Rückfrage' : 'Noch offen'}
            </span>
          </div>
          <div className="spacer" />
          {!voll && istBeirat && !abgeschlossen && (
            <>
              {dokument.pruefstatus === 'geprueft' ? (
                <button className="btn ghost sm" onClick={() => onStatus(dokument.id, 'offen')} title="Prüfvermerk zurücknehmen">
                  Zurücknehmen
                </button>
              ) : (
                <button className="btn primary sm" onClick={() => onStatus(dokument.id, 'geprueft')}>
                  ✓ Geprüft
                </button>
              )}
              <button className="btn ghost sm" onClick={() => onRueckfrage(dokument.id)}>
                ? Rückfrage
              </button>
            </>
          )}
          {onZurueck && (
            <button className="btn ghost sm" onClick={onZurueck} title="Vorherige Unterlage">
              ← Zurück
            </button>
          )}
          {onWeiter && (
            <button className="btn ghost sm" onClick={onWeiter} title="Nächste Unterlage">
              Weiter →
            </button>
          )}
          <button
            className="iconbtn"
            aria-label={voll ? 'Auf halbe Breite verkleinern' : 'Auf den ganzen Bildschirm ausweiten'}
            title={voll ? 'Halbe Breite' : 'Vollbild'}
            aria-pressed={voll}
            onClick={() => vollSetzen(!voll)}
          >
            {voll ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 3v6H3M15 21v-6h6M3 15h6v6M21 9h-6V3" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9V3h6M21 15v6h-6M21 3l-7 7M3 21l7-7" />
              </svg>
            )}
          </button>
          <button className="iconbtn" aria-label="Schließen" onClick={onSchliessen}>
            ✕
          </button>
        </div>

        {markierteStellen.length > 0 && (
          <div className="dok-stellen">
            <span className="s" style={{ margin: 0 }}>Markierte Stellen:</span>
            {markierteStellen.map(({ k, m }, i) => (
              <button
                key={k.id}
                className={`chip klickbar${hervorhebung === m ? ' aktiv' : ''}`}
                title={k.text}
                onClick={() => setHervorhebung(hervorhebung === m ? null : m)}
              >
                {i + 1} · S. {m.seite} · {k.text.length > 40 ? `${k.text.slice(0, 40)}…` : k.text}
              </button>
            ))}
          </div>
        )}
        <div className="dok-koerper">
          <div className="dok-blatt">
            {assistent !== false && (
              <div className={`dok-frage-leiste${frageOffen ? ' offen' : ''}`}>
                <button className="dok-frage-schalter" onClick={() => setFrageOffen((o) => !o)} aria-expanded={frageOffen}>
                  <AssistentSymbol />
                  <span>Frage zum Dokument</span>
                  {gespraech.length > 0 && !frageOffen && <span className="badge">{gespraech.filter((n) => n.rolle === 'assistent').length}</span>}
                  <span className="spacer" />
                  <span className="dok-frage-pfeil" aria-hidden="true">
                    {frageOffen ? '▾' : '▴'}
                  </span>
                </button>
                {frageOffen && (
                  <div className="dok-frage-inhalt">
                    <AssistentGespraech
                      pruefungId={pruefungId}
                      bezug={{ dokumentId: dokument.id }}
                      verlauf={gespraech}
                      onVerlauf={setGespraech}
                      verfuegbar={assistent}
                      kompakt
                      autoFokus
                      platzhalter="z. B. „Wie hoch ist die Zuführung zur Rücklage?“"
                      vorschlaege={['Worum geht es in diesem Dokument?', 'Welche Beträge fallen auf?']}
                    />
                  </div>
                )}
              </div>
            )}
            {istPdf ? (
              <PdfAnsicht
                belegId={dokument.id}
                url={url}
                hervorhebung={hervorhebung}
                drehung={drehung}
                onDrehen={setDrehung}
                onMarkierung={!abgeschlossen ? (r) => onRueckfrage(dokument.id, r) : undefined}
              />
            ) : istBild ? (
              <div className="pdfscroll">
                <img src={url} alt={dokument.dateiname} style={{ maxWidth: '100%', display: 'block', margin: '0 auto' }} />
              </div>
            ) : (
              <div className="leer card" style={{ margin: 'auto' }}>
                <h3>Keine Vorschau</h3>
                <p>
                  Diese Datei lässt sich hier nicht anzeigen.{' '}
                  <a href={url} download={dokument.dateiname}>
                    Herunterladen
                  </a>
                </p>
              </div>
            )}
          </div>

          {voll && (
          <aside className="dok-seite">
            <section className="dok-karte">
              <h4>Dokumentdetails</h4>
              <dl className="dok-details">
                <dt>Datei</dt>
                <dd className="dok-details-datei" title={dokument.dateiname}>{dokument.dateiname}</dd>
                <dt>Bereich</dt>
                <dd>{kategorie?.bezeichnung ?? '—'}</dd>
                <dt>Prüfschritt</dt>
                <dd>{kategorie?.schritt ? `Schritt ${kategorie.schritt}` : '—'}</dd>
                <dt>Bereitgestellt</dt>
                <dd>{datum(dokument.hochgeladenAm)}</dd>
                <dt>Umfang</dt>
                <dd>
                  {dokument.seiten ? `${dokument.seiten} Seiten · ` : ''}
                  {groesseText(dokument.groesse)}
                </dd>
                <dt>Prüfstatus</dt>
                <dd>
                  <span className={`chip${dokument.pruefstatus === 'geprueft' ? ' ok' : dokument.pruefstatus === 'rueckfrage' ? ' warn' : ''}`}>
                    {dokument.pruefstatus === 'geprueft' ? 'Geprüft' : dokument.pruefstatus === 'rueckfrage' ? 'Rückfrage' : 'Noch offen'}
                  </span>
                </dd>
              </dl>
              {kategorie?.hinweis && <p className="s dok-hinweis">{kategorie.hinweis}</p>}
            </section>

            <section className="dok-karte">
              <h4>Ihr Prüfvermerk</h4>
              {darfVermerken ? (
                <>
                  <textarea
                    className="dok-vermerk"
                    value={vermerk}
                    onChange={(e) => {
                      setVermerk(e.target.value);
                      setGespeichert('nein');
                    }}
                    onBlur={vermerkSpeichern}
                    rows={3}
                    placeholder="Notiz für das Prüfprotokoll (optional) …"
                  />
                  <div className="dok-vermerk-stand" aria-live="polite">
                    {gespeichert === 'laeuft' ? 'Wird gespeichert …' : gespeichert === 'ja' ? 'Gespeichert' : vermerk !== (dokument.vermerk ?? '') ? 'Wird beim Verlassen des Feldes gespeichert' : ''}
                  </div>
                </>
              ) : (
                <p className="s" style={{ margin: '0 0 10px' }}>{dokument.vermerk ? `„${dokument.vermerk}"` : 'Kein Vermerk.'}</p>
              )}

              {istBeirat && !abgeschlossen ? (
                <div className="dok-aktionen">
                  {dokument.pruefstatus === 'geprueft' ? (
                    <button className="btn ghost" onClick={() => onStatus(dokument.id, 'offen')}>
                      Prüfvermerk zurücknehmen
                    </button>
                  ) : (
                    <button
                      className="btn primary"
                      onClick={async () => {
                        await vermerkSpeichern();
                        onStatus(dokument.id, 'geprueft');
                      }}
                    >
                      ✓ Als geprüft markieren
                    </button>
                  )}
                  <button className="btn ghost" onClick={() => onRueckfrage(dokument.id)}>
                    ? Rückfrage an Verwaltung
                  </button>
                </div>
              ) : (
                <div className="dok-status">
                  <Haken zustand={dokument.pruefstatus} gross />
                  <span>
                    {dokument.pruefstatus === 'geprueft'
                      ? `Geprüft${dokument.pruefstatusVon ? ` von ${dokument.pruefstatusVon}` : ''}${dokument.pruefstatusAm ? ` am ${datum(dokument.pruefstatusAm)}` : ''}`
                      : dokument.pruefstatus === 'rueckfrage'
                        ? 'Rückfrage gestellt'
                        : 'Noch offen'}
                  </span>
                </div>
              )}
            </section>

            <details className="dok-karte dok-historie-karte" open>
              <summary>
                <h4>Prüfhistorie</h4>
              </summary>
              <ol className="dok-historie">
                {historie.map((h, i) => (
                  <li key={i}>
                    <span>{h.was}</span>
                    <b>{datumZeit(h.wann)}</b>
                  </li>
                ))}
              </ol>
            </details>

            <a className="dok-download" href={url} download={dokument.dateiname}>
              Original herunterladen
            </a>
          </aside>
          )}
        </div>
      </div>
    </div>
  );
}

function datumZeit(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString('de-AT', { day: 'numeric', month: 'long', year: 'numeric' })} · ${d.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })}`;
}

export function groesseText(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toLocaleString('de-AT', { maximumFractionDigits: 1 })} MB`;
}
